/**
 * The staging list: matched units grouped by model code. A group header drags
 * (or click-toggles) its whole model; each row drags on its own. Dates can be
 * overridden per model or per unit, narrowing the batch window.
 */

import { GripVertical, CalendarDays, Check } from 'lucide-react';
import { formatDayRange } from '@/lib/unitCodeParser';
import {
  PARENT_GROUP_TARGET,
  type BulkAssignGroup,
  type BulkAssignUnit,
  type DateWindow,
} from './types';
import { PARENT_GROUP_COLOR, siteColorAt, tint } from './targetStyles';
import type { DateScope } from './useBulkAssign';

interface UnitStagingListProps {
  pool: BulkAssignUnit[];
  modelLabels: Record<string, string>;
  staged: Record<string, string>;
  targetGroupId: string;
  parentGroupName: string;
  sites: BulkAssignGroup[];
  batch: DateWindow;
  modelDates: Record<string, DateWindow>;
  unitDates: Record<string, DateWindow>;
  lineDates: Record<string, DateWindow>;
  dateScope: DateScope | null;
  effectiveDates: (unit: BulkAssignUnit) => DateWindow;
  statusLabel: (status: string) => string;
  onToggleUnit: (unitId: string) => void;
  onStageMany: (unitIds: string[], target: string) => void;
  onUnstageMany: (unitIds: string[]) => void;
  onDragUnits: (unitIds: string[]) => void;
  onDragEnd: () => void;
  onToggleDateScope: (kind: 'unit' | 'model', key: string) => void;
  onSetScopedDate: (
    kind: 'unit' | 'model',
    key: string,
    field: 'start' | 'end',
    value: string
  ) => void;
  onClearScopedDate: (kind: 'unit' | 'model', key: string) => void;
}

function DateEditor({
  window: value,
  onStart,
  onEnd,
  onClear,
}: {
  window: DateWindow;
  onStart: (v: string) => void;
  onEnd: (v: string) => void;
  onClear: () => void;
}) {
  return (
    <div className="flex items-end gap-2 rounded-md border border-[#8FB560]/30 bg-[#8FB560]/[0.06] p-2">
      <label className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-[9px] font-medium uppercase tracking-wider text-muted-foreground">
          Start
        </span>
        <input
          type="date"
          value={value.start}
          onChange={(e) => onStart(e.target.value)}
          className="h-8 w-full rounded-md border border-border bg-background px-2 text-xs text-foreground outline-none [color-scheme:dark]"
        />
      </label>
      <label className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-[9px] font-medium uppercase tracking-wider text-muted-foreground">
          End
        </span>
        <input
          type="date"
          value={value.end}
          onChange={(e) => onEnd(e.target.value)}
          className="h-8 w-full rounded-md border border-border bg-background px-2 text-xs text-foreground outline-none [color-scheme:dark]"
        />
      </label>
      <button
        type="button"
        onClick={onClear}
        className="h-8 flex-none rounded-md border border-border px-2.5 text-[11px] text-muted-foreground hover:text-foreground"
      >
        Reset
      </button>
    </div>
  );
}

function dateChipClass(custom: boolean, unset: boolean): string {
  const base =
    'flex h-[22px] flex-none items-center gap-1 whitespace-nowrap rounded-full px-2 text-[10px] tabular-nums transition-colors hover:border-[#8FB560] hover:text-[#8FB560]';
  if (custom) return `${base} border border-[#8FB560]/45 bg-[#8FB560]/[0.12] text-[#8FB560]`;
  if (unset) return `${base} border border-dashed border-muted-foreground/50 text-muted-foreground`;
  return `${base} border border-border text-muted-foreground`;
}

export function UnitStagingList(props: UnitStagingListProps) {
  const {
    pool,
    modelLabels,
    staged,
    targetGroupId,
    parentGroupName,
    sites,
    modelDates,
    unitDates,
    lineDates,
    dateScope,
    batch,
    effectiveDates,
    statusLabel,
    onToggleUnit,
    onStageMany,
    onUnstageMany,
    onDragUnits,
    onDragEnd,
    onToggleDateScope,
    onSetScopedDate,
    onClearScopedDate,
  } = props;

  const describeTarget = (target: string) => {
    if (target === PARENT_GROUP_TARGET) {
      return { name: parentGroupName || 'Production', color: PARENT_GROUP_COLOR };
    }
    const index = sites.findIndex((s) => s.id === target);
    return {
      name: index === -1 ? 'Site' : sites[index].name,
      color: index === -1 ? PARENT_GROUP_COLOR : siteColorAt(index),
    };
  };

  // Model order follows first appearance in the pool, so a pasted list keeps
  // roughly the order it was written in.
  const order: string[] = [];
  const byModel = new Map<string, BulkAssignUnit[]>();
  pool.forEach((unit) => {
    if (!byModel.has(unit.modelCode)) {
      byModel.set(unit.modelCode, []);
      order.push(unit.modelCode);
    }
    byModel.get(unit.modelCode)!.push(unit);
  });

  return (
    <div className="flex flex-col gap-2.5">
      {order.map((modelCode) => {
        const items = byModel.get(modelCode)!;
        const ids = items.map((u) => u.id);
        const allStaged = ids.every((id) => staged[id]);
        const ownDates = modelDates[modelCode];
        const modelWindow = ownDates ?? batch;
        const modelEditing = dateScope?.kind === 'model' && dateScope.key === modelCode;
        const toggleModel = () =>
          allStaged ? onUnstageMany(ids) : onStageMany(ids, PARENT_GROUP_TARGET);

        return (
          <div key={modelCode} className="flex flex-col gap-0.5">
            <div
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('text/plain', ids.join(','));
                e.dataTransfer.effectAllowed = 'move';
                onDragUnits(ids);
              }}
              onDragEnd={onDragEnd}
              onClick={toggleModel}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleModel();
                }
              }}
              role="button"
              tabIndex={0}
              aria-pressed={allStaged}
              title={allStaged ? 'Take this whole model out' : 'Add this whole model'}
              className="-mx-1.5 flex cursor-grab items-center justify-between gap-2 rounded-md px-4 py-1 transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                <GripVertical className="h-3 w-3 text-muted-foreground/60" />
                {(modelLabels[modelCode] ?? modelCode)} · {items.length}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleDateScope('model', modelCode);
                }}
                title="Dates for this model"
                className={dateChipClass(!!ownDates, !modelWindow.start && !modelWindow.end)}
              >
                <CalendarDays className="h-[11px] w-[11px] flex-none" />
                {formatDayRange(modelWindow.start, modelWindow.end) || 'Set dates'}
              </button>
            </div>

            {modelEditing && (
              <DateEditor
                window={modelWindow}
                onStart={(v) => onSetScopedDate('model', modelCode, 'start', v)}
                onEnd={(v) => onSetScopedDate('model', modelCode, 'end', v)}
                onClear={() => onClearScopedDate('model', modelCode)}
              />
            )}

            {items.map((unit) => {
              const target = staged[unit.id];
              const isStaged = Boolean(target);
              const destination = isStaged ? describeTarget(target) : null;
              const own = unitDates[unit.id];
              const window = effectiveDates(unit);
              const editing = dateScope?.kind === 'unit' && dateScope.key === unit.id;
              const keepChip = Boolean(own || lineDates[unit.id] || editing);

              let tag = '';
              let tagClass = 'hidden';
              let tagColor: string | undefined;
              if (isStaged && targetGroupId && destination) {
                tag = destination.name;
                tagClass = 'max-w-[112px] flex-none truncate text-[10px]';
                tagColor = destination.color;
              } else if (unit.status !== 'active') {
                tag = statusLabel(unit.status);
                tagClass = 'flex-none text-[10px] text-yellow-400';
              } else if (targetGroupId && unit.parentGroupId === targetGroupId) {
                tag = 'Already here';
                tagClass = 'flex-none text-[10px] text-muted-foreground/70';
              }

              return (
                <div key={unit.id} className="flex flex-col gap-0.5">
                  <div
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('text/plain', unit.id);
                      e.dataTransfer.effectAllowed = 'move';
                      onDragUnits([unit.id]);
                    }}
                    onDragEnd={onDragEnd}
                    onClick={() => onToggleUnit(unit.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onToggleUnit(unit.id);
                      }
                    }}
                    role="checkbox"
                    aria-checked={isStaged}
                    tabIndex={0}
                    aria-label={`${unit.unitCode}${isStaged && destination ? `, staged for ${destination.name}` : ''}`}
                    className="group flex cursor-grab items-center gap-2 rounded-md px-2.5 py-[7px] transition-[filter,background-color,border-color] hover:brightness-125 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    style={{
                      border: `1px solid ${isStaged && destination ? tint(destination.color, 0.45) : 'hsl(var(--border))'}`,
                      borderLeft:
                        isStaged && destination
                          ? `3px solid ${destination.color}`
                          : '1px solid hsl(var(--border))',
                      background:
                        isStaged && destination
                          ? tint(destination.color, 0.08)
                          : 'hsl(var(--background))',
                    }}
                  >
                    <GripVertical className="h-3 w-3 flex-none text-muted-foreground/60" />
                    <span
                      aria-hidden="true"
                      className={`flex h-4 w-4 flex-none items-center justify-center rounded ${
                        isStaged
                          ? 'border border-[#8FB560] bg-[#8FB560]'
                          : 'border border-muted-foreground/50'
                      }`}
                    >
                      {isStaged && <Check className="h-[11px] w-[11px] text-black" strokeWidth={3.5} />}
                    </span>
                    <span className="flex-none font-mono text-[13px] text-foreground">
                      {unit.unitCode}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground">
                      {(unit.parentGroupName ?? 'Unassigned')} · {unit.companyName}
                    </span>
                    {tag && (
                      <span className={tagClass} style={tagColor ? { color: tagColor } : undefined}>
                        {tag}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleDateScope('unit', unit.id);
                      }}
                      title="Dates for this unit"
                      className={`${dateChipClass(!!own, !window.start && !window.end)} ${
                        keepChip ? '' : 'opacity-0 group-hover:opacity-100'
                      }`}
                    >
                      <CalendarDays className="h-[11px] w-[11px] flex-none" />
                      {formatDayRange(window.start, window.end) || 'Set dates'}
                    </button>
                  </div>

                  {editing && (
                    <DateEditor
                      window={window}
                      onStart={(v) => onSetScopedDate('unit', unit.id, 'start', v)}
                      onEnd={(v) => onSetScopedDate('unit', unit.id, 'end', v)}
                      onClear={() => onClearScopedDate('unit', unit.id)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
